import React, { useState, useRef } from 'react'

export type ObsidianGlassCardVariant = 'default' | 'solution' | 'pain' | 'bento'

export interface ObsidianGlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: ObsidianGlassCardVariant
  spotlight?: boolean
  innerStyle?: React.CSSProperties
  innerClassName?: string
}

const CSS = `
.wst-obsidian-card {
  position: relative;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: var(--space-3, 12px);
  box-sizing: border-box;
  overflow: hidden;
  transition: border-color var(--motion-fast, 150ms) ease,
              box-shadow var(--motion-fast, 150ms) ease;
}
.wst-obsidian-card:hover {
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 12px 40px -10px rgba(0, 0, 0, 0.7);
}

.wst-obsidian-card__spotlight {
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  transition: opacity var(--motion-fast, 150ms) ease;
}

.wst-obsidian-card__inner {
  position: relative;
  z-index: 2;
  background: var(--surface-obsidian-card, #070A11);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 20px;
  padding: var(--space-6, 24px);
  box-shadow: inset 0 1px 1px 0 rgba(255, 255, 255, 0.08);
  box-sizing: border-box;
}

/* VARIANTS */
.wst-obsidian-card--solution .wst-obsidian-card__inner {
  border-color: rgba(56, 191, 255, 0.25);
  box-shadow: inset 0 1px 1px 0 rgba(56, 191, 255, 0.3),
              0 0 30px -10px rgba(56, 191, 255, 0.15);
}

.wst-obsidian-card--pain .wst-obsidian-card__inner {
  opacity: 0.65;
  filter: grayscale(0.2);
}
.wst-obsidian-card--pain:hover .wst-obsidian-card__inner {
  opacity: 0.85;
}

.wst-obsidian-card--bento {
  padding: var(--space-2, 8px);
}
.wst-obsidian-card--bento .wst-obsidian-card__inner {
  padding: var(--space-5, 20px);
  border-radius: 18px;
}
`

if (typeof document !== 'undefined' && !document.getElementById('wst-obsidian-css')) {
  const s = document.createElement('style')
  s.id = 'wst-obsidian-css'
  s.textContent = CSS
  document.head.appendChild(s)
}

export const ObsidianGlassCard: React.FC<ObsidianGlassCardProps> = ({
  variant = 'default',
  spotlight = true,
  className = '',
  style = {},
  innerStyle = {},
  innerClassName = '',
  children,
  ...rest
}) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0, opacity: 0 })
  const cardRef = useRef<HTMLDivElement>(null)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!spotlight || !cardRef.current) return
    const rect = cardRef.current.getBoundingClientRect()
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      opacity: 1,
    })
  }

  const handleMouseLeave = () => {
    if (!spotlight) return
    setMousePos((prev) => ({ ...prev, opacity: 0 }))
  }

  const cls = ['wst-obsidian-card', `wst-obsidian-card--${variant}`, className]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      ref={cardRef}
      className={cls}
      style={style}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      {spotlight && (
        <div
          className="wst-obsidian-card__spotlight"
          style={{
            opacity: mousePos.opacity,
            background: `radial-gradient(350px circle at ${mousePos.x}px ${mousePos.y}px, rgba(56, 191, 255, 0.08), transparent 75%)`,
          }}
        />
      )}
      <div className={`wst-obsidian-card__inner ${innerClassName}`} style={innerStyle}>
        {children}
      </div>
    </div>
  )
}
